import {
  Button,
  DrawerStepperProgress,
  Label,
  Select,
  Text,
} from '@mercai/clever'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddressForm } from '@/components'
import { useValidationErrors } from '@/helpers'
import { AddressList } from '@/pages/business/components'
import {
  QUERIES_ADMIN,
  useAddressList,
  useBffCreateAddress,
  useEditAddress,
} from '@/services'
import { AddressProps, QuotationRequestDeliveryEnum } from '@/types'
import { useQueryClient } from '@tanstack/react-query'

import { shemaAddressItem } from './drawer-address.constants'
import styles from './drawer-address.module.css'
import { DrawerAddressProps } from './drawer-address.type'

export const DrawerAddress = ({
  isOpen,
  onHandleOpen,
  address,
  buyerProfileUuid,
  setAddress,
  onSubmit,
  onBack,
  deliveryOption,
  setDeliveryOption,
}: DrawerAddressProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const [internalAddress, setInternalAddress] = useState<Partial<AddressProps>>(
    address || {},
  )

  const [internalDeliveryOption, setInternalDeliveryOption] = useState<
    keyof typeof QuotationRequestDeliveryEnum
  >(deliveryOption || 'receive_at_address')

  const queryClient = useQueryClient()

  const {
    isValid: isValidAddressItem,
    errors: addressItemErrors,
    onHandleListenErrors: onHandleListenErrorsAddressItem,
  } = useValidationErrors(shemaAddressItem(t), internalAddress)

  const { data: dataAddresses } = useAddressList(buyerProfileUuid)

  const { mutateAsync: mutateAsyncNewAddress } = useBffCreateAddress()

  const { mutateAsync: mutateAsyncEditAddress } = useEditAddress(
    internalAddress?.uuid || '',
  )

  const onHandleNewAddress = ({
    onNavigateToNextStep,
  }: {
    onNavigateToNextStep: (route: string) => void
  }) => {
    onNavigateToNextStep('addressForm')
  }

  const onSubmitAddress = () => {
    setAddress(internalAddress)

    setDeliveryOption?.(internalDeliveryOption)

    onSubmit?.()

    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.ADDRESS.module, buyerProfileUuid],
    })
  }

  const onSubmitNewOrUpdateAddress = async ({
    onNavigateToPreviousStep,
  }: {
    onNavigateToPreviousStep: () => void
  }) => {
    if (isValidAddressItem) {
      const payload: Partial<AddressProps> = {
        ...internalAddress,
        profile: internalAddress?.profile || buyerProfileUuid,
        uuid: undefined,
        updatedAt: undefined,
        createdAt: undefined,
      }

      const response = !internalAddress?.uuid
        ? await mutateAsyncNewAddress(payload)
        : await mutateAsyncEditAddress(payload)

      if (response.success) {
        onNavigateToPreviousStep()

        setAddress(response?.data as Partial<AddressProps>)

        queryClient.invalidateQueries({
          queryKey: [QUERIES_ADMIN.ADDRESS.module, buyerProfileUuid],
        })
      }
    } else {
      onHandleListenErrorsAddressItem(true)
    }
  }

  const onSelectDeliveryOption = (
    value: keyof typeof QuotationRequestDeliveryEnum,
  ) => {
    setInternalDeliveryOption(value)

    if (value === 'pick_up') {
      setInternalAddress({})
    }
  }

  const onHandleEditAddress = (
    address: AddressProps,
    onNavigateToNextStep: (route: string) => void,
  ) => {
    setInternalAddress(address)
    onNavigateToNextStep('addressForm')
  }

  useEffect(() => {
    if (address) setInternalAddress(address)
    if (deliveryOption) setInternalDeliveryOption(deliveryOption)
  }, [address, deliveryOption])

  return (
    <DrawerStepperProgress
      initialStep="root"
      totalSteps={1}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      translations={{
        optional: '',
      }}
      title={t('addressListTitle')}
      steps={{
        root: {
          stepNumber: 1,
          isOptional: true,
          backNavigate: 'root',
          component: ({ onNavigateToNextStep }) => (
            <>
              <div className={styles['select-delivery-option']}>
                <Text
                  variant="text-large-bold"
                  className={styles['select-delivery-option-title']}
                >
                  {t('addressListDeliveryOptionTitle')}
                </Text>
                <Label>{t('addressListDeliveryOptionLabel')}</Label>
                <Select
                  value={internalDeliveryOption}
                  options={[
                    ...Object.keys(QuotationRequestDeliveryEnum).map(
                      (item) => ({
                        label: tEnums(
                          `logistic_shipment_delivery_option_${item}`,
                        ),
                        value: item,
                      }),
                    ),
                  ]}
                  onChange={(value) => {
                    onSelectDeliveryOption(
                      value as QuotationRequestDeliveryEnum,
                    )
                  }}
                  placeholder={t('orderDataDeliveryOptionPlaceholder')}
                />
              </div>
              {(!internalDeliveryOption ||
                internalDeliveryOption === 'receive_at_address') && (
                <AddressList
                  onClickNewAddress={() =>
                    onHandleNewAddress({ onNavigateToNextStep })
                  }
                  onChangeAddressSelected={(address) =>
                    setInternalAddress(address)
                  }
                  addressSelected={internalAddress as AddressProps}
                  addresses={dataAddresses?.data?.addresses || []}
                  onEditAddress={() =>
                    onHandleEditAddress(
                      internalAddress as AddressProps,
                      onNavigateToNextStep,
                    )
                  }
                />
              )}
            </>
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <div className={styles.actions}>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('navigateButtonCancel')}
              </Button>

              <div className={styles.actions} style={{ width: 'auto' }}>
                {onBack && (
                  <Button variant="success-light" onClick={onBack}>
                    {t('navigateButtonBack')}
                  </Button>
                )}

                <Button onClick={() => onSubmitAddress()}>
                  {t('navigateButtonFinishAndContinue')}
                </Button>
              </div>
            </div>
          ),
        },
        addressForm: {
          stepNumber: 1,
          title: t('addressFormTitle'),
          isOptional: false,
          backNavigate: 'root',
          component: (
            <AddressForm
              onChangeValues={setInternalAddress}
              values={internalAddress}
              errors={addressItemErrors as Record<string, string>}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <div className={styles.actions}>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('navigateButtonBack')}
              </Button>

              <Button
                onClick={() =>
                  onSubmitNewOrUpdateAddress({ onNavigateToPreviousStep })
                }
              >
                {t('navigateButtonFinish')}
              </Button>
            </div>
          ),
        },
      }}
    />
  )
}
